<template>
  <div
    ref="backdrop"
    class="bottom-up-modal__backdrop"
    :class="{'isShow': isShow, 'onTop': !aniFinish}"
  >
    <!-- 開啟後要觸發 focus 但 isShow 會觸發 re-render 讓 focus 跑回 body -->
    <!-- 所以用一個靜態的 mainWrapper 觸發 focus -->
    <div
      ref="mainWrapper"
      class="bottom-up-modal__main-wrapper"
      tabindex="1"
    >
      <div
        ref="main"
        class="bottom-up-modal__main"
        :class="{'isShow': isShow, 'hidden': aniFinish, 'persistent': persistent, 'pre-expand': contentExpand}"
      >
        <div
          class="bottom-up-modal__title-section"
          :class="{'hide-line': hideHeaderLine}"
        >
          <h4
            v-if="title"
            class="bottom-up-modal__title"
          >
            {{ title }}
          </h4>
          <h4
            v-if="subTitle"
            class="bottom-up-modal__subtitle"
          >
            {{ subTitle }}
          </h4>
          <div class="close-container">
            <span
              class="close"
              @click="close"
            />
          </div>
        </div>
        <div
          class="bottom-up-modal__content-section"
          :class="{'has-title': title}"
        >
          <div class="content-section__wrapper">
            <div class="content-section__content">
              <slot name="content" />
            </div>
            <div
              class="content-section__btn-container"
              :class="{'hide-line': hideFooterLine}"
            >
              <slot name="footer" />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import { mapState } from 'vuex'
// 手機及平板以上的顯示方式不同
export default {
  props: {
    propShow: {
      type: Boolean,
      default: false
    },
    propTitle: {
      type: String,
      default: ''
    },
    propSubtitle: {
      type: String,
      default: ''
    },
    persistent: {
      type: Boolean,
      default: false
    },
    contentExpand: {
      type: Boolean,
      default: false
    },
    hideHeaderLine: {
      type: Boolean,
      default: false
    },
    hideFooterLine: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      isShow: this.propShow,
      title: this.propTitle,
      subTitle: this.propSubtitle,
      /** 確保在關閉動畫結束前，抱持在最上層 */
      aniFinish: true
    }
  },
  computed: {
    ...mapState(['bottomUpDestroyTimeout'])
  },
  watch: {
    propShow (val) {
      if (!val) {
        document.documentElement.style.overflow = ''
        this.close()
      } else {
        document.documentElement.style.overflow = 'hidden'
        this.isShow = true
        this.aniFinish = false
        this.$refs.mainWrapper.focus()
      }
    },
    propTitle (title) {
      this.title = title || ''
    },
    isShow (val) {
      if (!val) {
        document.documentElement.style.overflow = ''
        this.close()
      } else {
        document.documentElement.style.overflow = 'hidden'
        this.isShow = true
        this.aniFinish = false
        this.$refs.mainWrapper.focus()
      }
    }
  },
  mounted () {
    this.$refs.mainWrapper.addEventListener('keydown', this.escapeKeyListener)
    this.$refs.backdrop.addEventListener('click', this.handleClick)
  },
  beforeDestroy () {
    this.$refs.mainWrapper.removeEventListener('keydown', this.escapeKeyListener)
    this.$refs.backdrop.removeEventListener('click', this.handleClick)
    if (this.bottomUpDestroyTimeout) {
      clearTimeout(this.bottomUpDestroyTimeout)
      this.$store.commit('setBottomUpDestroyTimeout', null)
    }
    const newTimeout = window.setTimeout(
      () => {
        if (!document.querySelector('.bottom-up-modal__backdrop.isShow')) {
          document.documentElement.style.overflow = ''
        }
        this.$store.commit('setBottomUpDestroyTimeout', null)
      }, 700
    )
    this.$store.commit('setBottomUpDestroyTimeout', newTimeout)
  },
  methods: {
    escapeKeyListener (e) {
      if (e && e.key === 'Escape') {
        this.persistCloseHandler()
      }
    },
    persistCloseHandler () {
      if (this.persistent) {
        this.persistentAnime()
        return
      }
      this.close()
    },
    close () {
      this.isShow = false
      window.setTimeout(() => {
        this.$emit('close', this.isShow)
        this.aniFinish = true
      }, 150)
    },
    handleClick (event) {
      if (!this.$refs.main) return
      const style = window.getComputedStyle(event.target)
      if (style.display === 'none') return
      const dialogDimensions = this.$refs.main.getBoundingClientRect()
      if (
        event.clientX < dialogDimensions.left ||
        event.clientX > dialogDimensions.right ||
        event.clientY < dialogDimensions.top ||
        event.clientY > dialogDimensions.bottom
      ) {
        this.persistCloseHandler()
      }
    },
    persistentAnime () {
      const main = this.$refs.main
      if (!main) return
      main.classList.add('animated')
      setTimeout(_ => main.classList.remove('animated'), 230)
    }
  }
}
</script>
<style lang="scss" scoped>
.bottom-up-modal {
  &__backdrop {
    z-index: -1;
    position: fixed;
    bottom: 0;
    left: 0;
    width: calc(var(--vw, 1vw) * 100);
    height: calc(var(--vh, 1vh) * 100);
    background-color: rgba(0, 0, 0, 0%);
    transition: background-color .2s ease-out;

    &.isShow {
      background-color: rgba(0, 0, 0, 40%);
    }

    &.onTop {
      z-index: 200;
    }
  }

  &__main-wrapper {
    width: 100%;
    height: 100%;

    display: flex;
    flex-direction: column;
    justify-content: flex-end;
    flex-wrap: nowrap;
  }

  &__main {
    height: 100%;
    max-height: fit-content;
    background-color: #fff;

    display: flex;
    flex-direction: column;
    flex-wrap: nowrap;
    box-shadow: 0px 11px 15px -7px rgba(0, 0, 0, 0.2), 0px 24px 38px 3px rgba(0, 0, 0, 0.14), 0px 9px 46px 8px rgba(0, 0, 0, 0.12);
  }

  &__title-section {
    position: relative;
    width: 100%;
    padding: 32px 0 14.5px;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: 5px;

    // divider
    &::after {
      content: '';
      position: absolute;
      bottom: 0;
      width: calc(100% - 80px);
      height: 1px;
      background-color: #EAEAEA;
    }

    &.hide-line::after {
      content: none;
    }
  }

  &__title {
    text-align: center;
    color: $fontColor;
    font-size: pxTorem(18px);
  }
  &__subtitle {
    width: 80%;
    max-width: fit-content;
    color: $darkGrayColor;
    font-size: pxTorem(15px);
    line-height: pxTorem(20px);
  }
}

.content-section {

  &__wrapper {
    height: 100%;
    max-height: calc(var(--vh, 1vh) * 100 - 72px * 2 - 103.5px);
    display: flex;
    flex-direction: column;
  }

  &__content {
    // padding: 0 25px;
    overflow: auto;
  }

  &__btn-container {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    > * {
      flex: 1;
    }

    &::after {
      content: '';
      position: absolute;
      top: 0;
      left: -10px;
      width: calc(100% + 20px);
      height: 1px;
      background-color: #EAEAEA;
    }

    &.hide-line::after {
      content: none;
    }
  }
}

.close {
  width: 16px;
  height: 16px;
  border-radius: 100%;
  cursor: pointer;
  position: relative;
  display: grid;
  place-items: center;

  &::before, &::after {
    content: '';
    position: absolute;
    border-radius: 1px;
    width: 2px;
    height: 22px;
    background-color: $fontColor;
    transform-origin: center;
  }

  &::before {
    transform: rotate(-45deg);
  }
  &::after {
    transform: rotate(45deg);
  }
}

.close-container {
  position: absolute;
  top: 20px;
  right: 20px;
}

// mobile above
@media screen and (min-width: $mobileWidth) {
  .bottom-up-modal {
    &__main {
      position: absolute;
      margin: 0;
      top: calc(#{$headerHeight} + 30px);
      left: 50%;
      width: calc(100vw - 300px);
      min-width: $mobileWidth;
      max-width: 850px;
      height: fit-content;
      min-height: 300px;
      max-height: calc(var(--vh, 1vh) * 100 - #{$headerHeight}*2 - 30px);
      border-radius: 5px;

      transition: all .2s ease-out;
      transform-origin: 0 center;
      transform: scale(.6) translate(-50%, 0);
      opacity: 0;
      &.isShow {
        transform: scale(1) translate(-50%, 0);
        opacity: 1;
      }

      &.hidden {
        visibility: hidden;
      }
    }

    &__content-section {
      overflow: auto;
    }
  }
  .close-container {
    top: 24px;
    right: 24px;
  }
  .close {
    background: transparent;
    &::before, &::after {
      background-color: #000;
    }
  }
  .content-section__btn-container {
    &::after {
      content: none;
    }
    > * {
      flex: initial;
    }
  }
  // style for persistent
  .persistent.animated {
    animation: persistClose .2s;
  }

  @keyframes persistClose {
    0% {
      transform: scale(1) translate(-50%, 0);
    }
    50% {
      transform: scale(102%) translate(-50%, 0);
    }
    100% {
      transform: scale(1) translate(-50%, 0);
    }
  }
}
// mobile below
@media screen and (max-width: $mobileWidth) {
  .bottom-up-modal {
    &__main {
      max-height: fit-content;
      height: calc(var(--vh, 1vh) * 100 - 31px);
      border-radius: 10px 10px 0 0;
      transform: translateY(100%);
      transition: transform .2s ease-in-out;
      &.isShow {
        transform: translateY(0);
      }
    }

    &__content-section {
      max-height: calc(100% - 48px);

      &.has-title {
        max-height: calc(100% - 75px);
      }
    }

    &__title-section {
      padding: 24px 0;

      &::after {
        width: 100%;
      }
    }
  }

  .content-section {
    &__wrapper {
      max-height: 100%;
    }

    &__btn-container::after {
      width: 100%;
      left: 0;
    }
  }

  // 手機版 內容全展開
  .bottom-up-modal__main.pre-expand {
    max-height: initial;
    .content-section {
      &__wrapper {
        max-height: initial;
        height: 100%;
      }
      &__content {
        flex: 1;
      }
    }
  }

  // style for persistent
  .persistent.animated {
    transform-origin: center bottom;
    animation: persistClose .2s;
  }

  @keyframes persistClose {
    0% {
      transform: scale(1);
    }
    50% {
      transform: scale(102%);
    }
    100% {
      transform: scale(1);
    }
  }
}

</style>

<!-- 使用範例

<v-btn @click="showBottomUpModal = true">
  showModal
</v-btn>
<bottom-up-modal
  :propShow="showBottomUpModal"
  propTitle='BottomUpModal'
  @close="showBottomUpModal = false"
>
  <template #content>
    <p>
      Lorem ipsum dolor sit amet consectetur adipisicing elit. Nam voluptates dignissimos delectus rerum, illum nihil qui est unde praesentium quasi ex esse aspernatur exercitationem asperiores magnam beatae a, voluptas nesciunt.<br />
      Lorem ipsum dolor, sit amet consectetur adipisicing elit. Facilis fugiat molestiae blanditiis autem quidem ex? Tempora nisi hic quia dignissimos ullam dolorem illo placeat exercitationem, eveniet est. Tempora, error ab?<br />
      Lorem ipsum dolor, sit amet consectetur adipisicing elit. Nemo inventore, et, odit officiis reiciendis modi quae iure nostrum temporibus consectetur optio animi officia, eum fugit est. Reiciendis in odit rerum?<br />
      Lorem ipsum dolor sit amet consectetur adipisicing elit. Similique animi dolorem dignissimos consequatur itaque cupiditate aut! Incidunt enim itaque dolores repudiandae quod. Accusamus ipsum at adipisci minus? Labore, cumque sit.<br />
      Lorem ipsum dolor sit amet consectetur adipisicing elit. Voluptatum, neque. Libero fugiat, tenetur quos inventore laudantium numquam perferendis. Cumque aperiam ad saepe praesentium est laudantium quo nam corporis quidem eveniet.<br />
      Lorem ipsum dolor sit amet consectetur adipisicing elit. Placeat commodi incidunt itaque amet, eveniet corrupti delectus natus pariatur soluta, voluptates fugit sequi aspernatur magnam accusantium sunt, animi dicta consectetur eos.<br />
      Lorem ipsum dolor, sit amet consectetur adipisicing elit. Pariatur, quis eum officiis harum repellat ipsum quas. Adipisci illum eos, molestias, ut cumque tempora natus optio, sed possimus porro doloribus ipsam.<br />
      Lorem ipsum dolor sit, amet consectetur adipisicing elit. Obcaecati, doloribus! Debitis saepe quo, enim nam voluptatibus ad? Ab excepturi earum itaque in iste debitis totam inventore vitae consequuntur, placeat nesciunt!<br />
      Lorem ipsum dolor sit amet consectetur adipisicing elit. Libero inventore, tempore unde aut illo optio facilis velit distinctio! Ea eos eveniet deleniti ab ratione ipsa voluptatum et dolor amet adipisci.<br />
      Lorem ipsum dolor sit amet consectetur adipisicing elit. Numquam ut recusandae, amet perspiciatis ea quos sed ducimus architecto ipsum id accusantium quia pariatur laborum dolorem asperiores. Nisi ab ipsa unde.<br />
    </p>
  </template>
  <template #footer>
    <v-btn @click="sayHello">sayHello</v-btn>
  </template>
</bottom-up-modal>

-->
